/**
 * Studio Zuiver: <delivery-date> (blocks/_delivery-date.liquid).
 * Recomputes "Bestel binnen {countdown} en ontvang het naar verwachting {date}" with the visitor's clock in
 * Europe/Amsterdam and refreshes it every minute. Liquid renders the first version with the shop's
 * clock; this keeps a cached page correct. The rules come as data attributes from the theme settings:
 *   data-cutoff       "16:00"
 *   data-ship-days    Sun..Sat mask, e.g. "0111110"
 *   data-deliver-days Sun..Sat mask, e.g. "0111111"
 *   data-processing   extra shipping days before hand-over
 *   data-transit      delivery days after shipping
 *   data-holidays     "2026-12-25,2026-12-26"
 *   data-text-before / data-text-after / data-relative
 */
const TZ = 'Europe/Amsterdam';
const WEEKDAYS = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
const MONTHS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

/** Current wall-clock time in the shop's time zone as plain numbers. */
function nowInShopTz(override) {
  // data-now="2026-10-02T17:30" (shop-local) lets you test a scenario without waiting for it.
  if (override) {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(override);
    if (m) return { y: +m[1], m: +m[2], d: +m[3], h: +m[4], min: +m[5] };
  }
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { y: get('year'), m: get('month'), d: get('day'), h: get('hour') % 24, min: get('minute') };
}

/** Calendar day arithmetic on UTC dates (no DST surprises). */
const dayUTC = (y, m, d) => new Date(Date.UTC(y, m - 1, d));
const addDays = (date, n) => new Date(date.getTime() + n * 86400000);
const ymd = (date) => date.toISOString().slice(0, 10);

class DeliveryDate extends HTMLElement {
  #timer = null;

  connectedCallback() {
    this.update();
    this.#timer = setInterval(() => this.update(), 60000);
    document.addEventListener('visibilitychange', this.#onVisible);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    document.removeEventListener('visibilitychange', this.#onVisible);
  }

  #onVisible = () => {
    if (document.visibilityState === 'visible') this.update();
  };

  update() {
    const textEl = this.querySelector('[ref="deliveryText"]');
    if (!textEl) return;

    const shipMask = this.dataset.shipDays || '0111110';
    const deliverMask = this.dataset.deliverDays || '0111111';
    const holidays = new Set((this.dataset.holidays || '').split(',').map((s) => s.trim()).filter(Boolean));
    const processing = Number(this.dataset.processing || 0);
    const transit = Math.max(1, Number(this.dataset.transit || 1));
    const [cutH, cutM] = (this.dataset.cutoff || '16:00').split(':').map(Number);
    const cutoffMinutes = cutH * 60 + (cutM || 0);

    const now = nowInShopTz(this.dataset.now);
    const today = dayUTC(now.y, now.m, now.d);
    const nowMinutes = now.h * 60 + now.min;
    const isShipDay = (date) => shipMask[date.getUTCDay()] === '1' && !holidays.has(ymd(date));
    const isDeliverDay = (date) => deliverMask[date.getUTCDay()] === '1' && !holidays.has(ymd(date));

    let beforeCutoff = false;
    let ship = today;
    if (isShipDay(today) && nowMinutes < cutoffMinutes) {
      beforeCutoff = true;
    } else {
      do { ship = addDays(ship, 1); } while (!isShipDay(ship));
    }
    for (let done = 0; done < processing; ) {
      ship = addDays(ship, 1);
      if (isShipDay(ship)) done += 1;
    }
    let delivery = ship;
    for (let done = 0; done < transit; ) {
      delivery = addDays(delivery, 1);
      if (isDeliverDay(delivery)) done += 1;
    }

    let dateText = `${WEEKDAYS[delivery.getUTCDay()]} ${delivery.getUTCDate()} ${MONTHS[delivery.getUTCMonth()]}`;
    if (this.dataset.relative === 'true') {
      const diff = Math.round((delivery.getTime() - today.getTime()) / 86400000);
      if (diff === 1) dateText = `morgen (${dateText})`;
      else if (diff === 2) dateText = `overmorgen (${dateText})`;
    }

    let line;
    if (beforeCutoff) {
      const left = cutoffMinutes - nowMinutes;
      const h = Math.floor(left / 60);
      const m = left % 60;
      const countdown = h > 0 ? `${h} uur${m > 0 ? ` en ${m} min` : ''}` : `${m} ${m === 1 ? 'minuut' : 'minuten'}`;
      line = (this.dataset.textBefore || '').replace('{countdown}', countdown);
    } else {
      line = this.dataset.textAfter || '';
    }
    line = line.replace('{date}', dateText).replace('{cutoff}', this.dataset.cutoff || '');
    if (textEl.textContent !== line) textEl.textContent = line;
  }
}

if (!customElements.get('delivery-date')) {
  customElements.define('delivery-date', DeliveryDate);
}
